import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Activity, KeyRound, Users, Send, RefreshCw, Loader2 } from "lucide-react";
import { partnerApi, formatPartnerError } from "./partnerApi";
import "./PartnerPortal.css";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat().format(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 16) + " UTC";
}

export default function PartnerOverview() {
  const { me } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/overview");
      setData(data);
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="pp-overview">
      <h1 className="hw-pp__page-title">Overview</h1>
      <p className="hw-pp__page-sub">
        Real-time snapshot of your integration with HarborWell Connect.
        Refresh anytime for the latest usage.
      </p>

      {err && <div className="hw-pp__error">{err}</div>}
      {loading && !data && (
        <div style={{ color: "#5EEAD4" }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
        </div>
      )}

      {data && (
        <>
          {data.webhook_sla && data.webhook_sla.state !== "not_configured" && (
            <div className="hw-pp__panel" data-testid="pp-sla-badge"
                  style={{ borderColor: data.webhook_sla.state === "ok" ? "#22C55E"
                              : data.webhook_sla.state === "degraded" ? "#F59E0B" : "#94A3B8" }}>
              <h3>
                <span>Webhook Health · 24h SLA</span>
                <small>
                  Delivered: {data.webhook_sla.delivered_24h} · DLQ: {data.webhook_sla.dlq_24h}
                </small>
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
                <div style={{ flex: 1, height: "0.55rem", background: "rgba(15,23,42,0.7)",
                                borderRadius: "0.3rem", overflow: "hidden" }}>
                  <div style={{ width: `${data.webhook_sla.success_rate_pct}%`, height: "100%",
                                  background: data.webhook_sla.state === "ok" ? "#22C55E"
                                    : data.webhook_sla.state === "degraded" ? "#F59E0B" : "#3B82F6",
                                  transition: "width 0.6s ease" }} />
                </div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700,
                                color: "#F8FAFC", minWidth: "70px", textAlign: "right" }}
                     data-testid="pp-sla-pct">
                  {data.webhook_sla.success_rate_pct}%
                </div>
              </div>
            </div>
          )}

          <div className="hw-pp__stat-grid">
            <div className="hw-pp__stat" data-testid="pp-stat-requests">
              <div className="hw-pp__stat-label">Total API requests</div>
              <div className="hw-pp__stat-value">{fmt(data.usage.total_requests)}</div>
            </div>
            <div className="hw-pp__stat" data-testid="pp-stat-active-keys">
              <div className="hw-pp__stat-label">Active API keys</div>
              <div className="hw-pp__stat-value">{fmt(data.usage.active_keys)}</div>
            </div>
            <div className="hw-pp__stat" data-testid="pp-stat-webhooks">
              <div className="hw-pp__stat-label">Webhook subscriptions</div>
              <div className="hw-pp__stat-value">{fmt(data.webhook_subscriptions)}</div>
            </div>
            <div className="hw-pp__stat" data-testid="pp-stat-users">
              <div className="hw-pp__stat-label">Portal users</div>
              <div className="hw-pp__stat-value">
                {fmt(data.users.active)}
                <span style={{ fontSize: "0.7rem", color: "#94A3B8", marginLeft: "0.35rem" }}>
                  active / {data.users.total} total
                </span>
              </div>
            </div>
            <div className="hw-pp__stat">
              <div className="hw-pp__stat-label">Last API call</div>
              <div className="hw-pp__stat-value" style={{ fontSize: "0.9rem" }}>
                {fmtDate(data.usage.last_used_at)}
              </div>
            </div>
            <div className="hw-pp__stat">
              <div className="hw-pp__stat-label">Status</div>
              <div className="hw-pp__stat-value" style={{ fontSize: "0.95rem" }}>
                <span className="hw-pp__pill hw-pp__pill--active">
                  {me.partner_org.status || "active"}
                </span>
              </div>
            </div>
          </div>

          <div className="hw-pp__panel">
            <h3>
              <span><KeyRound className="w-4 h-4"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem" }} />
                Allowed scopes</span>
              <small>{data.allowed_scopes?.length || 0} scopes negotiated with HarborWell</small>
            </h3>
            {/* Iter 77.2 — grouped read-only display with risk + description */}
            {(data.allowed_scopes_grouped && data.allowed_scopes_grouped.length > 0) ? (
              <div data-testid="pp-allowed-scopes" style={{ display: "grid", gap: "0.6rem" }}>
                {data.allowed_scopes_grouped.map((g) => (
                  <div
                    key={g.key}
                    data-testid={`pp-scope-group-${g.key}`}
                    style={{
                      border: "1px solid #E5E5E0",
                      borderRadius: "0.55rem",
                      padding: "0.55rem 0.7rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", marginBottom: "0.25rem" }}>
                      <strong style={{ fontSize: "0.8rem" }}>{g.label}</strong>
                      <span style={{ fontSize: "0.68rem", color: "#64748B" }}>
                        {g.scopes.length} scope{g.scopes.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    {g.helper && (
                      <div style={{ fontSize: "0.68rem", color: "#64748B", marginBottom: "0.35rem" }}>
                        {g.helper}
                      </div>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                      {g.scopes.map((s) => (
                        <span
                          key={s.key}
                          className="hw-pp__scope-chip"
                          data-testid={`pp-scope-chip-${s.key}`}
                          title={s.description || s.key}
                          style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <span style={{ fontFamily: "monospace" }}>{s.key}</span>
                          <span
                            data-testid={`pp-scope-risk-${s.risk}`}
                            style={{
                              fontSize: "0.6rem",
                              padding: "0.05rem 0.3rem",
                              borderRadius: "0.3rem",
                              background: s.risk === "high" ? "#FEE2E2" : s.risk === "medium" ? "#FEF3C7" : "#DCFCE7",
                              color: s.risk === "high" ? "#991B1B" : s.risk === "medium" ? "#92400E" : "#166534",
                              fontWeight: 600,
                            }}
                          >
                            {(s.risk || "medium").toUpperCase()}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="pp-allowed-scopes">
                {(data.allowed_scopes || []).map((s) => (
                  <span key={s} className="hw-pp__scope-chip">{s}</span>
                ))}
                {(data.allowed_scopes || []).length === 0 && (
                  <span style={{ color: "#64748B", fontSize: "0.85rem" }}>No scopes yet.</span>
                )}
              </div>
            )}
          </div>

          <div className="hw-pp__panel">
            <h3>
              <span><Activity className="w-4 h-4"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem" }} />
                Recent events</span>
              <button className="hw-pp__btn" onClick={load}
                       style={{ padding: "0.25rem 0.6rem" }}>
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </h3>
            {(data.recent_events || []).length === 0 ? (
              <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>
                No recent audit events. Actions taken here + by HarborWell staff on your org appear in this feed.
              </div>
            ) : (
              <table className="hw-pp__table" data-testid="pp-recent-events">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Resource</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_events.slice(0, 10).map((e, idx) => (
                    <tr key={idx}>
                      <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                        {(e.at || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="hw-pp__mono">{e.action}</td>
                      <td>{e.actor_email || "—"}</td>
                      <td>{e.resource_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
